"use client";

import { Box, Typography } from "@mui/material";
import { brand, logoColors, navy } from "@/theme/tokens";
import { DARK } from "@/theme/scheme";
import { LOGO_MARK_HEIGHT, LOGO_MARK_SRC, LOGO_MARK_WIDTH } from "@/theme/logo";

/** Davnai's flowing play-ribbon mark. `size` is its height. */
export function LogoMark({ size = 28 }: { size?: number }) {
  return (
    <Box component="img" src={LOGO_MARK_SRC} width={LOGO_MARK_WIDTH} height={LOGO_MARK_HEIGHT} alt="" aria-hidden sx={{ height: size, width: size, objectFit: "contain", flexShrink: 0, display: "block" }} />
  );
}

/**
 * "Davnai" with the dot of the last i in the mark's yellow (owner decision 2026-10-28). Read as one word: the
 * dotless ı and the dot are hidden from screen readers, which hear "Davnai".
 */
export function Wordmark() {
  const name = brand.name;
  return (
    <>
      <Box component="span" sx={{ position: "absolute", width: "1px", height: "1px", margin: "-1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>{name}</Box>
      <Box component="span" aria-hidden>
        {name.slice(0, -1)}
        <Box component="span" sx={{ position: "relative", display: "inline-block", lineHeight: 1 }}>
          ı
          <Box component="span" sx={{ position: "absolute", left: "50%", top: "0.005em", width: "0.21em", height: "0.21em", ml: "-0.105em", borderRadius: "50%", bgcolor: logoColors.markYellow }} />
        </Box>
      </Box>
    </>
  );
}

/**
 * Logo: mark + Davnai wordmark. `onDark` for dark surfaces, where the
 * wordmark is white whatever the colour scheme.
 */
export default function Logo({ size = 28, showTagline = false, onDark = false }: { size?: number; showTagline?: boolean; onDark?: boolean }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: size > 40 ? 1.75 : 1.1 }}>
      <LogoMark size={Math.round(size * 1.4)} />
      <Box sx={{ lineHeight: 1 }}>
        <Typography
          component="span"
          sx={{ display: "block", fontFamily: '"Poppins", "Inter", sans-serif', fontWeight: 800, fontSize: size * 0.78, letterSpacing: "-0.075em", color: onDark ? "common.white" : logoColors.charcoal, ...(onDark ? {} : { [DARK]: { color: "common.white" } }) }}
        >
          <Wordmark />
        </Typography>
        {showTagline && (
          <Typography component="span" variant="caption" sx={{ display: "block", mt: 0.35, color: onDark ? navy.textMuted : "text.secondary" }}>
            CREATE · EDIT · PUBLISH
          </Typography>
        )}
      </Box>
    </Box>
  );
}
